'use client'

import { AnimatePresence, motion } from 'motion/react'
import React from 'react'

import type { LocationUniversityTravelStat } from '@/lib/mock/locationPages'
import { getMobileTravelStatLabel } from '@/lib/travelStats'
import { cn } from '@/utilities/ui'

type LocationHeroUniversityTravelStatsTheme = 'dark' | 'light'

type LocationHeroUniversityTravelStatsProps = {
  compact?: boolean
  destinationLabel?: string
  stats: LocationUniversityTravelStat[]
  universityName: string
  theme?: LocationHeroUniversityTravelStatsTheme
}

const destinationClassNames: Record<LocationHeroUniversityTravelStatsTheme, string> = {
  dark: 'mb-4 font-outfit text-base font-medium leading-snug text-white/95',
  light: 'mb-4 font-outfit text-base font-medium leading-snug text-dark-800/80',
}

const valueClassNames: Record<LocationHeroUniversityTravelStatsTheme, string> = {
  dark: 'inline-block bg-gradient-to-r from-white/95 to-white/50 bg-clip-text font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-transparent',
  light:
    'inline-block bg-gradient-to-r from-dark-800/90 to-dark-800/50 bg-clip-text font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-transparent',
}

const labelClassNames: Record<LocationHeroUniversityTravelStatsTheme, string> = {
  dark: 'mt-1 block whitespace-pre-line font-outfit text-xs leading-snug text-white/95 md:text-sm',
  light:
    'mt-1 block whitespace-pre-line font-outfit text-xs leading-snug text-dark-800/60 md:text-sm',
}

const dividerClassNames: Record<LocationHeroUniversityTravelStatsTheme, string> = {
  dark: 'border-l border-white/10 pl-3 md:pl-6',
  light: 'border-l border-black/10 pl-3 md:pl-6',
}

function AnimatedTravelValue({
  compact = false,
  delay = 0,
  theme,
  value,
}: {
  compact?: boolean
  delay?: number
  theme: LocationHeroUniversityTravelStatsTheme
  value: string
}) {
  return (
    <span
      className={cn(
        'relative inline-grid overflow-hidden [perspective:900px]',
        compact ? 'h-7 md:h-7' : 'h-8',
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={value}
          animate={{ opacity: 1, rotateX: 0, y: 0 }}
          className={cn(
            valueClassNames[theme],
            'col-start-1 row-start-1 origin-[50%_100%]',
            compact && 'md:text-[1.75rem]',
          )}
          exit={{ opacity: 0, rotateX: 75, y: -10 }}
          initial={{ opacity: 0, rotateX: -75, y: 10 }}
          transition={{
            delay,
            duration: 0.42,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

export function LocationHeroUniversityTravelStats({
  compact = false,
  destinationLabel,
  stats,
  universityName,
  theme = 'dark',
}: LocationHeroUniversityTravelStatsProps) {
  if (stats.length === 0) {
    return null
  }

  return (
    <div>
      {destinationLabel ? (
        <p
          className={cn(
            destinationClassNames[theme],
            compact && 'md:mb-3',
          )}
        >
          {destinationLabel}
        </p>
      ) : null}
      <dl
        aria-label={`Travel times from ${universityName}`}
        className={cn(
          'grid w-full max-w-full grid-cols-3 gap-y-6 md:inline-grid md:w-max',
          compact ? 'md:gap-y-5' : 'md:gap-y-8',
        )}
      >
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className={cn(
            index % 3 !== 0 && dividerClassNames[theme],
            index % 3 !== 0 && compact && 'md:pl-4',
            index % 3 !== 2 && (compact ? 'pr-2 md:pr-4' : 'pr-2 md:pr-6'),
          )}
        >
          <dt className="sr-only">{stat.label}</dt>
          <dd aria-atomic="true" aria-live="polite">
            <AnimatedTravelValue
              compact={compact}
              delay={index * 0.05}
              theme={theme}
              value={stat.value}
            />
            <span className={labelClassNames[theme]}>
              <span className="md:hidden">{getMobileTravelStatLabel(stat.label)}</span>
              <span className="hidden md:inline">{stat.label}</span>
            </span>
          </dd>
        </div>
      ))}
      </dl>
    </div>
  )
}
